import React from 'react';
import styled from '@emotion/styled';
import Responsive from '../../../../ui/utils/Responsive';
import { StyledVehiclesHeading } from './VehiclesEmptyStateContainer';
import { useTranslation } from '../../../shared/i18n/translate/useTranslation';
import {
	ArrowButton,
	ErrorVehicleIcon,
	H2,
	H3,
	RefreshIcon,
	StyledIconButton,
} from '../../../../ui';
import { IVehicle } from '../../../shared/api/gateway/models/Vehicle';

interface ICardPageProps {
	error?: Error;
	listComponent: React.ReactNode;
	onRetry: () => void;
	vehiclesList: IVehicle[];
	addVehicleButtonText?: string;
	onAddVehicle?: () => void;
}

export const VehiclesCardContainer = ({
	error,
	listComponent,
	onRetry,
	vehiclesList,
	addVehicleButtonText,
	onAddVehicle,
}: ICardPageProps) => {
	const { t } = useTranslation();

	// Show "Add New Vehicle" button only if vehicle count is above this threshold
	const SHOW_ADD_NEW_VEHICLE_TILE_THRESHOLD = 3;

	return (
		<>
			{error ? (
				<StyledVehicleErrorContainer>
					<ErrorVehicleIcon />
					<StyledVehicleErrorInfo>
						<ErrorHeading>
							{t('account_vehicles_error_state_heading')}
						</ErrorHeading>
						<ErrorText>{t('account_vehicles_error_state_explainer')}</ErrorText>
						<RefreshButton onClick={onRetry}>
							{t('account_vehicles_error_try_again')}
							<RefreshIcon />
						</RefreshButton>
					</StyledVehicleErrorInfo>
				</StyledVehicleErrorContainer>
			) : (
				<>
					<StyledVehicleHeadingContainer>
						<StyledVehiclesHeading>
							{t('account_vehicles_label')}
						</StyledVehiclesHeading>
						{vehiclesList.length > SHOW_ADD_NEW_VEHICLE_TILE_THRESHOLD && (
							<ArrowButton secondary type="submit" onClick={onAddVehicle}>
								{addVehicleButtonText}
							</ArrowButton>
						)}
					</StyledVehicleHeadingContainer>
					<StyledCardContainer>{listComponent}</StyledCardContainer>
				</>
			)}
		</>
	);
};

const StyledVehicleErrorContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
`;

export const StyledCardContainer = styled.div`
	width: 100%;
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 464px));
	align-items: center;
	gap: 24px;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		gap: 16px;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		grid-template-columns: 1fr;
		gap: 16px;
	}
`;

const StyledVehicleErrorInfo = styled.div`
	display: flex;
	flex-direction: column;
	max-width: 400px;
`;

const ErrorHeading = styled(H3.withComponent(H2))`
	margin: 0;
`;

const ErrorText = styled.p`
	margin-bottom: 15px;
`;

const RefreshButton = styled(StyledIconButton)`
	gap: 5px;
	width: fit-content;
`;

const StyledVehicleHeadingContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 18px;
`;
